<template>
  <section class="basicExamFindingRecord">
    <template>
      <ul
        class="pl-0 basicExamFindingRecord__descript"
        v-if="
          getFirstLocationItem ||
          getDescOptions('size') ||
          getGastroDescSelectInput.length
        "
      >
        <BasicRecordFieldItem
          data-test="desc-location"
          v-for="(item, key) in getFirstLocationItem"
          :class="[
            {
              'basicExamFindingRecord__descript--fullhalf':
                (getDescOptions('size') && key !== 'dist') ||
                (EXAMINATION_TYPE_OF_GASTROSCOPY === examType &&
                  key === 'loca'),
            },
            {
              'basicExamFindingRecord__descript--fullfull':
                Object.keys(getFirstLocationItem).length === 1 &&
                getGastroDescSelectInput.length === 0 &&
                !getDescOptions('size'),
            },
            {
              'basicExamFindingRecord__descript--fullhalf':
                Object.keys(getFirstLocationItem).length === 2 &&
                getGastroDescSelectInput.length === 0 &&
                !getDescOptions('size'),
            },
          ]"
          :key="key"
          tag="li"
          :inputType="item.inputType"
          :title="item.title"
          :value="item.value"
          :options="item.questionID && getLocationQuestion(item.questionID)"
          :disabled="!item.questionID"
          v-bind="item.option"
          @change="
            $emit('locationChange', {
              value: $event,
              column: key,
              id: item.locationId,
            })
          "
        />
        <BasicRecordFieldItem
          v-if="getDescOptions('size')"
          tag="li"
          inputType="text"
          title="Size"
          suffix="cm"
          textType="number"
          :value="getDescOptions('size') && getDescOptions('size').value"
          :class="{
            'basicExamFindingRecord__descript--fullhalf':
              getFirstLocationItem &&
              Object.keys(getFirstLocationItem).length === 1 &&
              !getGastroDescSelectInput.length,
          }"
          @change="
            $emit('change', {
              newValue: $event,
              ...getSize.config,
            })
          "
        />
        <template v-if="EXAMINATION_TYPE_OF_GASTROSCOPY === examType">
          <template v-for="desc in getGastroDescSelectInput">
            <!--
              KAN-58 防呆：clinic 面的 Form 欄是寫死的單選 chip；若這筆紀錄的 form 值是
              主檢查表單存的「各級別數量」編碼（F1:2;F2:1），單選會找不到對應 option 而顯示空、
              一旦醫師誤點就會把數量資料靜默覆蓋成單一 option id。此時改為【唯讀顯示數量 +
              提示去主表單編輯】，不渲染可寫入的單選，從源頭杜絕覆蓋（disabled 不會 emit change）。
            -->
            <BasicRecordFieldItem
              v-if="isFormEncoded(desc)"
              :key="desc.field + '-fq-readonly'"
              data-test="gastro-desc-form-readonly"
              class="h-100"
              :class="[
                {
                  'basicExamFindingRecord__descript--fullhalf':
                    !getFirstLocationItem ||
                    (getFirstLocationItem &&
                      Object.keys(getFirstLocationItem).length === 1),
                },
                {
                  'basicExamFindingRecord__descript--fullfull':
                    !getFirstLocationItem &&
                    getGastroDescSelectInput.length === 1,
                },
              ]"
              tag="li"
              inputType="text"
              :title="desc.title + '（' + $t('finding_words.form_quantity_clinic_readonly') + '）'"
              :value="formEncodedDisplay(desc)"
              :disabled="true"
            />
            <BasicRecordFieldItem
              v-else
              :key="desc.field"
              data-test="gastro-desc-specific"
              class="h-100"
              :class="[
                {
                  'basicExamFindingRecord__descript--fullhalf':
                    !getFirstLocationItem ||
                    (getFirstLocationItem &&
                      Object.keys(getFirstLocationItem).length === 1),
                },
                {
                  'basicExamFindingRecord__descript--fullfull':
                    !getFirstLocationItem &&
                    getGastroDescSelectInput.length === 1,
                },
              ]"
              tag="li"
              inputType="selectInput"
              :title="desc.title"
              :value="desc.items.value"
              :options="generateQList(desc.items.config.options)"
              @change="
                $emit('change', {
                  newValue: $event,
                  ...desc.items.config,
                })
              "
            />
          </template>
        </template>
      </ul>
      <section
        v-else
        data-test="descript--empty"
        class="bg-light w-100 h-100"
      ></section>
    </template>

    <ul class="pl-0 basicExamFindingRecord__intervention">
      <BasicRecordFieldItem
        v-if="getIntervenOptions('intervention')"
        title="Intervention"
        tag="li"
        bgColor="bg-warning-1"
        inputType="selectInput"
        :value="getIntervenOptions('intervention').value"
        :options="
          generateQList(getIntervenOptions('intervention').config.options)
        "
        @change="
          $emit('change', {
            newValue: $event,
            ...getIntervenOptions('intervention').config,
          })
        "
      />

      <!--
        KAN-8：specimen 瓶號改為下拉選單（A–D），語音填錯時醫師可直接手動修正。
        當瓶號為系統（語音/正規化）自動填入時，以黑底（bg-specimen-corrected）標記，
        提示醫師一眼看出「這格被系統改過、請核對」；手動改過後標記自動清除。
      -->
      <BasicRecordFieldItem
        :key="specimenFieldKey"
        tag="li"
        inputType="select"
        title="Specimen Label"
        :bgColor="isSpecimenLabelCorrected ? 'bg-specimen-corrected' : 'bg-warning-1'"
        :disabled="!getSpecimenLabel"
        :value="specimenLabelValue"
        :options="specimenLabelOptions"
        @change="
          $emit('change', {
            newValue: $event,
            ...getSpecimenLabel.config,
          })
        "
      />
    </ul>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import BasicRecordFieldItem from "./BasicRecordFieldItem.vue";
import { EXAMINATION_TYPE_OF_GASTROSCOPY } from "@/utils/examinations.config";
// KAN-8：specimen 瓶號白名單（client 鏡像，與 server/public/tool/specimen-label.js 同步）
import { SPECIMEN_LABEL_ALLOWED } from "@/utils/specimen-label";
// KAN-58：clinic 面防呆——偵測 form 欄是否為主檢查表單存的「各級別數量」編碼
import { isEncodedFormQuantity, formatForDisplay } from "@/utils/form-quantity";

export default {
  name: "BasicExamFindingRecord",
  components: {
    BasicRecordFieldItem,
  },
  props: {
    examType: Number,
    record: Object,
  },
  data() {
    return {
      EXAMINATION_TYPE_OF_GASTROSCOPY,
      gastroDesc: [
        {
          title: "L.A Grade",
          field: "laGrade",
          category: "L.A. grade",
        },
        {
          title: "Form",
          field: "form",
          category: "form",
        },
        {
          title: "Ulcer Stage",
          field: "ulcerStage",
          category: "ulcer stage",
        },
      ],
    };
  },
  computed: {
    ...mapGetters("examinations", [
      "getQuestionsList",
      "getSpecimenLabelCorrected",
      "getSpecimenLabelRevertNonce",
    ]),
    // KAN-8：specimen 瓶號下拉選項 = 空選項（清除誤填）+ 白名單瓶號。
    // 白名單來自 client 鏡像常數（@/utils/specimen-label），與後端 specimen-label.js 同步；
    // KAN-4 擴充瓶數時改鏡像常數即可，不需動這裡的組裝邏輯。
    specimenLabelOptions() {
      return [
        { value: "", text: "—" },
        ...SPECIMEN_LABEL_ALLOWED.map((letter) => ({ value: letter, text: letter })),
      ];
    },
    // KAN-8：下拉的 :key。record.id 讓各 record 獨立；revert nonce 在軟拒絕後變動 →
    // 強制此下拉 remount，丟棄 v-select 內部 lazyValue、重新吃 store 舊值（視覺回退）。
    specimenFieldKey() {
      const id = this.record ? this.record.id : "x";
      return `specimen-${id}-${this.getSpecimenLabelRevertNonce}`;
    },
    // 顯示值一律轉大寫：即使 DB 內是舊資料的小寫 a，畫面也顯示為 A（大寫正規化）。
    specimenLabelValue() {
      const v = this.getSpecimenLabel && this.getSpecimenLabel.value;
      return v ? String(v).toUpperCase() : "";
    },
    // 這筆 record 的 specimen 瓶號是否為系統自動填入（需顯示校正黑底標記）。
    isSpecimenLabelCorrected() {
      return !!(
        this.getSpecimenLabel &&
        this.record &&
        this.getSpecimenLabelCorrected[this.record.id]
      );
    },
    questionsByPart() {
      const qValues = this.record.questions;
      const result = Object.keys(qValues).reduce(
        (acc, field) => {
          const { part, questionID } = qValues[field];
          if (questionID) {
            const category =
              this.getQuestionsList &&
              this.getQuestionsList[questionID].category;
            qValues[field].category = category;
          }
          return field === "location" || part === "d"
            ? {
                description: [...acc.description, qValues[field]],
                intervention: acc.intervention,
              }
            : {
                description: acc.description,
                intervention: [...acc.intervention, qValues[field]],
              };
        },
        { description: [], intervention: [] }
      );
      return {
        description: this.sortQuestionByCategories(result.description),
        intervention: this.sortQuestionByCategories(result.intervention),
      };
    },
    generateQList() {
      return (options) => {
        if (!options) return [];
        return Object.keys(options).map((o) => ({
          value: o,
          text: options[o].charAt(0).toUpperCase() + options[o].slice(1),
        }));
      };
    },
    getGastroDescSelectInput() {
      let data = [];
      this.gastroDesc.forEach((desc) => {
        const items = this.getDescOptions(desc.category);
        if (items) {
          data.push({ ...desc, items });
        }
      });
      return data;
    },
    getFirstLocationItem() {
      const loca = this.questionsByPart.description
        ? this.questionsByPart.description.find((i) => i.category === "loca")
        : {};
      const data = {
        loca: {
          title: "Location",
          inputType: "select",
          value: "",
          option: {},
        },
        dist: {
          title:
            EXAMINATION_TYPE_OF_GASTROSCOPY === this.examType
              ? "From Incisor"
              : "Distance",
          inputType: "text",
          value: "",
          option: {
            textType: "number",
            suffix: "cm",
          },
        },
      };
      if (loca && loca.items) {
        Object.keys(data).forEach((key) => {
          const firstLoca = loca.items[0];
          if (loca.items[0].questions[key]) {
            data[key].value = firstLoca.questions[key]?.value;
            data[key].questionID = firstLoca.questions[key]?.questionID;
            data[key].locationId = firstLoca.id;
          } else {
            delete data[key];
          }
        });
        return data;
      }
      return null;
    },
    getLocationQuestion() {
      return (qID) => {
        if (!this.getQuestionsList || !qID || !this.getQuestionsList[qID])
          return [];
        const OPTIONS = this.getQuestionsList[qID].options;
        const result = Object.keys(OPTIONS).map((o) => ({
          value: o,
          text: OPTIONS[o].charAt(0).toUpperCase() + OPTIONS[o].slice(1),
        }));
        return result;
      };
    },
    getDescOptions() {
      return (category) => {
        const result = this.questionsByPart.description.find(
          (i) => i.category === category
        );
        return result && result.items[0];
      };
    },
    getIntervenOptions() {
      return (category) => {
        const result = this.questionsByPart.intervention.find(
          (i) => i.category === category
        );
        return result && result.items[0];
      };
    },
    getSpecimenLabel() {
      const result = this.questionsByPart.intervention.find(
        (i) => i.category === "patho"
      );
      return result
        ? result.items.find((i) => i.config.field === "label")
        : null;
    },
  },
  methods: {
    // KAN-58：clinic 面（胃鏡 desc 單選 chip）不得靜默覆蓋主檢查表單存的 form「各級別
    // 數量」編碼（如 F1:2;F2:1）。這兩個 helper 供 template 判斷是否改成唯讀顯示。
    isFormEncoded(desc) {
      return !!(
        desc &&
        desc.field === "form" &&
        desc.items &&
        isEncodedFormQuantity(desc.items.value)
      );
    },
    formEncodedDisplay(desc) {
      return desc && desc.items ? formatForDisplay(desc.items.value) : "";
    },
    sortQuestionByCategories(dataArray) {
      const categorymap = {
        // n: null,
        m: "maneuver", //
        l: "clo location",
        p: "patho",
        d: "device",
        i: "injection",
        ic: "intraoperative condition",
        o: "other",
      };
      let categoryList = {};

      dataArray.map((item) => {
        /**
         * 1. 若有questionID 將對應的問題裝進 item.config 裡
         * 2. 若 item.config 有值確認他是否有選擇 intervenOther
         * 3. item[0] 用來處理 locations
         * 4. 依據不同情境把 item 放進特定 key值 aka question
         * 5. 關鍵在handle catArray
         */

        if (item.questionID) {
          item.config =
            this.getQuestionsList && this.getQuestionsList[item.questionID];
        }

        if (item.config) {
          const textAreaFields = ["intervenOther"]; // 選擇 other 會有填寫 other 的input 匡
          textAreaFields.indexOf(item.config.field) >= 0 &&
            (item.config.inputType = "ta");
        }

        if (
          item[0] &&
          item[0].questions &&
          (item[0].questions.loca || item[0].questions.dist)
        ) {
          categoryList.loca = item;
        }

        const ifICSelect =
          item.category == "ic" && item.config.inputType == "s";
        const catName = categorymap[item.category];

        if (ifICSelect) {
          categoryList[item.config.question] = [];
          categoryList[item.config.question].push(item);
        } else if (catName) {
          !categoryList[catName] && (categoryList[catName] = []);
          categoryList[catName].push(item);
        } else if (item.questionID) {
          // 物件裡會顯示對應的物件名稱
          const question =
            this.getQuestionsList &&
            this.getQuestionsList[item.questionID].question;
          categoryList[question] = item;
        }
      });

      const catArray = Object.entries(categoryList).map(([key, value]) => {
        const items = Array.isArray(value) ? value : [value];
        return {
          category: key,
          items,
        };
      });

      return catArray;
    },
    getQuestionIDContent(questionID) {
      return this.getQuestionsList && this.getQuestionsList[questionID];
    },
  },
};
</script>

<style lang="scss">
.basicExamFindingRecord {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 0.5rem;
  &__descript {
    display: grid;
    column-gap: 0.5rem;
    row-gap: 0.5rem;
    grid-template-rows: repeat(2, 1fr);
    grid-template-columns: repeat(2, 1fr);
    &--fullhalf {
      grid-column: 1/3;
    }
    &--fullfull {
      grid-row: 1/3;
      grid-column: 1/3;
    }
  }
  &__intervention {
    display: grid;
    column-gap: 0.5rem;
    row-gap: 0.5rem;
  }
}

// KAN-8：specimen 瓶號校正標記（黑底）。用 .basic-field-item.bg-specimen-corrected 雙 class
// 提高權重，蓋過 BasicRecordFieldItem 內把文字設為 $primary 的規則，確保黑底上文字可讀（白字）。
.basic-field-item.bg-specimen-corrected {
  background-color: #1a1a1a !important;

  [data-test="field-title"],
  .v-select__selection,
  .v-select__selection--comma,
  input {
    color: #ffffff !important;
  }
}
</style>
